/* Botanical Tenant Layout — garden-press, organic, hand-typeset.

   What makes this different from Editorial:
   • Header is a centered "almanac plate" — circle-rule-circle-rule
     ornaments flank the studio name like a vintage botanical book frontispiece.
   • Wordmark in Fraunces, the soft-organic display serif. Even the
     active-nav underline is a hand-drawn double rule (a thin + a hairline)
     rather than the standard editorial single underline.
   • Nav buttons have rounded-2xl pill corners — the only template
     where pills appear, signalling the "garden-press" organic warmth.
   • Footer is an "Almanac · Volume" panel: three columns wrapped in a
     thin sage border, with each column titled in caps and the studio's
     contact info hand-set in Fraunces italic.
*/

import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useTenantTheme } from "@/hooks/useTenantTheme";
import { tenantHref } from "@/lib/tenant";
import TenantSignature from "@/components/tenant/TenantSignature";
import LumetierFooterCredit from "@/components/tenant/LumetierFooterCredit";
import TenantMobileNav from "@/components/tenant/TenantMobileNav";
import TenantWordmark from "@/components/tenant/TenantWordmark";
import { Instagram, Mail } from "lucide-react";

const NAV = [
  { to: "/",          label: "Home",      end: true },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/services",  label: "Services"  },
  { to: "/contact",   label: "Contact"   },
];

const DISPLAY = "'Fraunces', 'Cormorant Garamond', Georgia, serif";

/* Tiny botanical ornament — circle, rule, circle — used either side of the
   wordmark and as section separators. Pure SVG so it scales with type. */
function Ornament({ width = 80, color }) {
  return (
    <svg width={width} height={10} viewBox={`0 0 ${width} 10`} fill="none" aria-hidden>
      <circle cx="3" cy="5" r="2.5" fill={color} opacity="0.85" />
      <line x1="9" y1="5" x2={width - 9} y2="5" stroke={color} strokeOpacity="0.5" strokeWidth="0.75" />
      <circle cx={width - 3} cy="5" r="2.5" fill={color} opacity="0.85" />
    </svg>
  );
}

export default function BotanicalTenantLayout() {
  useTenantTheme();
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const year = new Date().getFullYear();

  return (
    <div
      className="min-h-screen antialiased"
      style={{
        fontFamily: "var(--tenant-sans)",
        background: "var(--tenant-bg, #F5F4EE)",
        color: "var(--tenant-fg, #2A2D24)",
      }}
      data-template="botanical"
    >
      {/* ── Almanac-plate masthead ────────────────────────────────────── */}
      <header
        className={`fixed top-0 inset-x-0 z-40 transition-all ${scrolled ? "py-3 backdrop-blur-md" : "py-5"}`}
        style={{
          background: scrolled
            ? "color-mix(in srgb, var(--tenant-bg, #F5F4EE) 92%, transparent)"
            : "color-mix(in srgb, var(--tenant-bg, #F5F4EE) 70%, transparent)",
          borderBottom: scrolled ? "1px solid var(--tenant-fg-line)" : "1px solid transparent",
        }}
        data-testid="botanical-tenant-nav"
      >
        {/* Mobile-only hamburger — keeps the centered ornament+wordmark
            symmetric on phones; auto-hides at md+. */}
        <div className="absolute right-3 top-3 md:hidden">
          <TenantMobileNav tone="light" ctaLabel="Reserve a bloom" studioName={studioName} />
        </div>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-center gap-4">
            <Ornament width={scrolled ? 50 : 90} color="var(--tenant-accent)" />
            <Link
              to={tenantHref("/")}
              className={`transition-all ${scrolled ? "text-lg" : "text-2xl lg:text-3xl"}`}
              style={{
                fontFamily: DISPLAY,
                color: "var(--tenant-fg)",
                letterSpacing: "0.02em",
              }}
              data-testid="botanical-tenant-wordmark"
            >
              <TenantWordmark
                logoUrl={settings?.logo_url}
                coverLogoUrl={settings?.cover_logo_url}
                tone="light"
                studioName={studioName}
                imgClassName={scrolled ? "h-7 w-auto" : "h-9 lg:h-12 w-auto"}
              >
                {studioName}
              </TenantWordmark>
            </Link>
            <Ornament width={scrolled ? 50 : 90} color="var(--tenant-accent)" />
          </div>
          {/* Nav as soft pills */}
          <nav className={`mt-3 hidden md:flex justify-center gap-1.5`} aria-label="Primary">
            {NAV.map((it) => (
              <NavLink
                key={it.to}
                to={tenantHref(it.to)}
                end={it.end}
                data-testid={`botanical-tenant-nav-${it.label.toLowerCase()}`}
                className="px-4 py-1.5 rounded-2xl text-[10px] uppercase tracking-[0.32em] transition-all"
                style={({ isActive }) => ({
                  color: isActive ? "color-mix(in srgb, var(--tenant-bg, #F5F4EE) 95%, white)" : "var(--tenant-fg-soft, rgba(42,45,36,0.6))",
                  background: isActive ? "var(--tenant-accent)" : "transparent",
                })}
              >
                {it.label}
              </NavLink>
            ))}
            <Link
              to={tenantHref("/booking")}
              className="ml-2 inline-flex items-center px-4 py-1.5 rounded-2xl text-[10px] uppercase tracking-[0.32em] border transition-colors hover:opacity-90"
              style={{ borderColor: "var(--tenant-accent)", color: "var(--tenant-accent)" }}
              data-testid="botanical-tenant-book-cta"
            >
              Reserve a Bloom →
            </Link>
          </nav>
        </div>
      </header>

      {/* ── Body ──────────────────────────────────────────────────────── */}
      <main key={location.pathname} className="pt-32 lg:pt-36">
        <Outlet />
      </main>

      {/* ── Almanac-volume footer ─────────────────────────────────────── */}
      <footer
        className="mt-16 pt-12 pb-10 border-t"
        style={{
          background: "var(--tenant-bg, #F5F4EE)",
          color: "var(--tenant-fg, #2A2D24)",
          borderTopColor: "var(--tenant-accent)",
        }}
        data-testid="botanical-tenant-footer"
      >
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <div className="flex justify-center mb-4">
            <Ornament width={120} color="var(--tenant-accent)" />
          </div>
          <div className="text-center text-[10px] uppercase tracking-[0.42em] mb-2" style={{ color: "var(--tenant-accent)" }}>
            Almanac · Volume {year - 2024}
          </div>
          <div className="text-center text-3xl lg:text-4xl mb-10" style={{ fontFamily: DISPLAY, color: "var(--tenant-fg)" }}>
            {studioName}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 border rounded-2xl px-8 py-8" style={{ borderColor: "var(--tenant-accent)", background: "color-mix(in srgb, var(--tenant-bg, #F5F4EE) 95%, white)" }}>
            <div className="text-center md:text-left">
              <div className="text-[10px] uppercase tracking-[0.42em] mb-2" style={{ color: "var(--tenant-accent)" }}>Studio</div>
              {settings?.tagline ? (
                <p className="text-sm italic leading-relaxed" style={{ fontFamily: DISPLAY, color: "var(--tenant-fg-soft)" }}>
                  {settings.tagline}
                </p>
              ) : (
                <p className="text-sm italic" style={{ color: "var(--tenant-fg-soft)" }}>{studioName} · {year}</p>
              )}
            </div>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-[0.42em] mb-2" style={{ color: "var(--tenant-accent)" }}>Correspond</div>
              <div className="text-sm space-y-1.5" style={{ color: "var(--tenant-fg-soft)" }}>
                {settings?.contact_email && (
                  <a href={`mailto:${settings.contact_email}`} className="inline-flex items-center gap-2 hover:opacity-100" style={{ opacity: 0.9 }}>
                    <Mail className="w-3.5 h-3.5" strokeWidth={1.25} /> {settings.contact_email}
                  </a>
                )}
                {settings?.contact_location && (
                  <div className="italic" style={{ fontFamily: DISPLAY }}>{settings.contact_location}</div>
                )}
              </div>
            </div>
            <div className="text-center md:text-right">
              <div className="text-[10px] uppercase tracking-[0.42em] mb-2" style={{ color: "var(--tenant-accent)" }}>Field Garden</div>
              <div className="flex md:justify-end justify-center gap-4" style={{ color: "var(--tenant-fg-soft)" }}>
                {settings?.instagram_url && (
                  <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="hover:opacity-100" style={{ opacity: 0.9 }}>
                    <Instagram className="w-4 h-4" strokeWidth={1.25} />
                  </a>
                )}
              </div>
            </div>
          </div>

          <div className="text-center text-[10px] uppercase tracking-[0.42em] mt-8" style={{ color: "var(--tenant-fg-mute)" }}>
            <span>© {year} {studioName}</span>
            <span className="opacity-50 mx-3">·</span>
            <LumetierFooterCredit className="hover:opacity-100" testid="botanical-lumetier-credit" />
          </div>
        </div>
      </footer>

      <TenantSignature tone="light" />
    </div>
  );
}
